import { useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { institutionalVerificationService } from "../../../services/institutional-verification.service";
import { useMe } from "../../../hooks/useMe";

const MAX_FILE_SIZE_MB = 5;
const PAGE_SIZE = 5;
const ALLOWED_TYPES = ["application/pdf"];

export default function MonitoringSection({
  institutionId,
  monitorings = [],
  isAllowedAction,
  onPreview,
  onDownload,
}) {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const [fileList, setFileList] = useState([]);
  const [page, setPage] = useState(1);
  const { data: me } = useMe();
  const inputBy = me?.data?.fullName || "";

  const { mutate: addMonitoring, isPending } = useMutation({
    mutationFn: (values) =>
      institutionalVerificationService.addFacilityMonitoring(institutionId, {
        monitoredAt: dayjs(values.monitoredAt).format(),
        purpose: values.purpose,
        document: values.document[0].originFileObj,
      }),
    onSuccess: () => {
      messageApi.success("Monitoring berhasil ditambahkan");
      form.resetFields();
      setFileList([]);
      queryClient.invalidateQueries({ queryKey: ["facility-data"] });
    },
    onError: (error) => {
      messageApi.error(
        error?.response?.data?.message || "Gagal menambahkan monitoring",
      );
    },
  });

  const columns = [
    {
      title: "No",
      align: "center",
      width: 50,
      render: (_, __, index) => (page - 1) * PAGE_SIZE + index + 1,
    },
    {
      title: "Tanggal & Waktu Monitoring",
      dataIndex: "monitoredAt",
      width: 220,
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Dokumen Bukti Monitoring",
      dataIndex: "document",
      render: (document) =>
        document?.filePath ? (
          <Flex align="center" gap={8}>
            <Typography.Text style={{ marginRight: "auto" }} ellipsis>
              {document.fileName}
            </Typography.Text>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() =>
                onDownload?.(
                  document.filePath,
                  document.fileName || "bukti-monitoring",
                )
              }
            >
              Unduh
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => onPreview?.(document.filePath)}
            >
              Lihat
            </Button>
          </Flex>
        ) : (
          "-"
        ),
    },
    {
      title: "Tujuan Monitoring",
      dataIndex: "purpose",
      width: 240,
      render: (val) => (
        <Typography.Paragraph
          style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
          ellipsis={{ rows: 2, expandable: "collapsible" }}
        >
          {val || "-"}
        </Typography.Paragraph>
      ),
    },
    {
      title: "Diinput Oleh",
      dataIndex: "updatedBy",
      width: 180,
      render: (val) => val || "-",
    },
  ];

  const beforeUpload = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      messageApi.error("Dokumen bukti monitoring harus berupa PDF");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      messageApi.error(`Ukuran dokumen maksimal ${MAX_FILE_SIZE_MB}MB`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return (
    <>
      {contextHolder}
      <Table
        size="small"
        bordered
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: monitorings.length,
          onChange: setPage,
          align: "end",
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        rowKey="uuid"
        columns={columns}
        dataSource={monitorings}
        scroll={{ x: 900 }}
        locale={{ emptyText: "Belum ada monitoring" }}
        style={{ marginBottom: 24 }}
      />

      {isAllowedAction && (
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => addMonitoring(values)}
        >
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Tambah Monitoring
          </Typography.Title>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="monitoredAt"
                label="Tanggal & Waktu Monitoring"
                rules={[
                  {
                    required: true,
                    message: "Tanggal & waktu monitoring harus diisi",
                  },
                ]}
              >
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: "100%" }}
                  disabledDate={(current) => current && current > dayjs()}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="document"
                label="Dokumen Bukti Monitoring"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList?.slice(-1) || []}
                rules={[
                  {
                    required: true,
                    message: "Dokumen bukti monitoring harus diunggah",
                  },
                ]}
                extra={`Hanya PDF. Maksimal ${MAX_FILE_SIZE_MB}MB`}
              >
                <Upload
                  accept=".pdf,application/pdf"
                  maxCount={1}
                  fileList={fileList}
                  beforeUpload={beforeUpload}
                  onChange={({ fileList: list }) => setFileList(list.slice(-1))}
                >
                  <Button icon={<UploadOutlined />}>Pilih Dokumen</Button>
                </Upload>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="purpose"
                label="Tujuan Monitoring"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Tujuan monitoring harus diisi",
                  },
                  {
                    max: 1000,
                    message: "Tujuan monitoring maksimal 1000 karakter",
                  },
                ]}
              >
                <Input placeholder="Masukkan tujuan monitoring" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Diinput Oleh">
                <Input value={inputBy} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Button type="primary" htmlType="submit" loading={isPending}>
            Simpan Monitoring
          </Button>
        </Form>
      )}
    </>
  );
}
